<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const whatsappCloudInboxes = computed(() =>
  (inboxes.value || []).filter(
    i => i.channel_type === 'Channel::Whatsapp' && i.provider === 'whatsapp_cloud'
  )
);

// ────────── Form state ──────────
const selectedInboxId = ref(null);
const templateName = ref('');
const category = ref('MARKETING');
const language = ref('es');
const submitting = ref(false);
const submitError = ref(null);
const submitSuccess = ref(false);

// Header
const headerType = ref('NONE'); // NONE | TEXT | IMAGE | VIDEO | DOCUMENT
const headerText = ref('');

// Body
const bodyText = ref('');
const bodyVariables = ref([]); // [{ index: 1, example: '' }]

// Footer
const footerText = ref('');

// Buttons
const buttons = ref([]); // { type: 'QUICK_REPLY'|'URL'|'PHONE_NUMBER', text, url?, phone? }

// ────────── Options ──────────
const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', icon: 'i-lucide-megaphone', desc: 'Promotions, offers, updates' },
  { value: 'UTILITY', label: 'Utility', icon: 'i-lucide-wrench', desc: 'Account updates, alerts' },
  { value: 'AUTHENTICATION', label: 'Authentication', icon: 'i-lucide-shield-check', desc: 'OTPs, verification codes' },
];

const LANGUAGES = [
  { value: 'es', label: 'Spanish (ES)' },
  { value: 'es_MX', label: 'Spanish (MX)' },
  { value: 'es_AR', label: 'Spanish (AR)' },
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'pt_BR', label: 'Portuguese (BR)' },
];

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'i-lucide-message-square-reply' },
  { value: 'URL', label: 'URL Button', icon: 'i-lucide-external-link' },
  { value: 'PHONE_NUMBER', label: 'Phone Number', icon: 'i-lucide-phone' },
];

// ────────── Body variable detection ──────────
function syncBodyVariables() {
  const matches = [...bodyText.value.matchAll(/\{\{(\d+)\}\}/g)];
  const usedIndices = [...new Set(matches.map(m => parseInt(m[1])))].sort((a, b) => a - b);
  bodyVariables.value = usedIndices.map(i => ({
    index: i,
    example: bodyVariables.value.find(v => v.index === i)?.example || '',
  }));
}

watch(bodyText, syncBodyVariables);

function insertVariable() {
  const nextIdx = (bodyVariables.value[bodyVariables.value.length - 1]?.index || 0) + 1;
  bodyText.value += `{{${nextIdx}}}`;
}

// ────────── Buttons management ──────────
function addButton(type) {
  if (buttons.value.length >= 3) return;
  buttons.value.push({ type, text: '', url: '', phone: '' });
}

function removeButton(idx) {
  buttons.value.splice(idx, 1);
}

// ────────── Live preview ──────────
const previewHeader = computed(() => {
  if (headerType.value === 'NONE') return null;
  if (headerType.value === 'TEXT') return { type: 'text', content: headerText.value || 'Header text' };
  return { type: 'media', format: headerType.value };
});

const previewBody = computed(() => {
  let text = bodyText.value;
  bodyVariables.value.forEach(v => {
    text = text.replaceAll(`{{${v.index}}}`, v.example ? `*${v.example}*` : `_{{${v.index}}}_`);
  });
  return text;
});

// Computed for v-html to avoid regex special chars in template attributes
const previewBodyHtml = computed(() => {
  if (!previewBody.value) return '';
  return previewBody.value
    .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
});

// ────────── Helpers ──────────
function varLabel(index) {
  return '{{' + index + '}}';
}

function varPlaceholder(index) {
  return 'Example for {{' + index + '}}';
}

// ────────── Build components payload ──────────
function buildComponents() {
  const components = [];

  if (headerType.value !== 'NONE') {
    const header = { type: 'HEADER', format: headerType.value };
    if (headerType.value === 'TEXT') header.text = headerText.value;
    components.push(header);
  }

  if (bodyText.value) {
    const body = { type: 'BODY', text: bodyText.value };
    if (bodyVariables.value.length > 0) {
      body.example = { body_text: [bodyVariables.value.map(v => v.example || `example${v.index}`)] };
    }
    components.push(body);
  }

  if (footerText.value) {
    components.push({ type: 'FOOTER', text: footerText.value });
  }

  if (buttons.value.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: buttons.value.map(b => {
        const btn = { type: b.type, text: b.text };
        if (b.type === 'URL') btn.url = b.url;
        if (b.type === 'PHONE_NUMBER') btn.phone_number = b.phone;
        return btn;
      }),
    });
  }

  return components;
}

// ────────── Validation ──────────
const validationErrors = computed(() => {
  const errors = [];
  if (!selectedInboxId.value) errors.push('Select a WhatsApp Cloud inbox');
  if (!templateName.value.trim()) errors.push('Template name is required');
  if (!/^[a-z0-9_]+$/.test(templateName.value)) errors.push('Name must be lowercase letters, numbers and underscores only');
  if (!bodyText.value.trim()) errors.push('Body text is required');
  if (bodyVariables.value.some(v => !v.example.trim())) errors.push('All variable examples are required');
  if (buttons.value.some(b => !b.text.trim())) errors.push('All button texts are required');
  if (buttons.value.some(b => b.type === 'URL' && !b.url.trim())) errors.push('URL buttons require a URL');
  if (buttons.value.some(b => b.type === 'PHONE_NUMBER' && !b.phone.trim())) errors.push('Phone buttons require a phone number');
  return errors;
});

// ────────── Submit ──────────
async function submit() {
  if (validationErrors.value.length > 0) return;
  submitting.value = true;
  submitError.value = null;
  try {
    await whatsappTemplatesAPI.create(selectedInboxId.value, {
      name: templateName.value,
      category: category.value,
      language: language.value,
      components: buildComponents(),
    });
    submitSuccess.value = true;
    setTimeout(() => router.push({ name: 'whatsapp_templates_index' }), 1500);
  } catch (e) {
    submitError.value = e.response?.data?.error || 'Error submitting template';
  } finally {
    submitting.value = false;
  }
}

onMounted(() => {
  store.dispatch('inboxes/get');
  if (whatsappCloudInboxes.value.length > 0) {
    selectedInboxId.value = whatsappCloudInboxes.value[0].id;
  }
});

watch(whatsappCloudInboxes, list => {
  if (!selectedInboxId.value && list.length > 0) selectedInboxId.value = list[0].id;
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full w-full min-w-0 bg-n-background overflow-hidden">
    <!-- Header -->
    <div class="flex items-center gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-1 shrink-0">
      <button
        class="flex items-center gap-1.5 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="router.push({ name: 'whatsapp_templates_index' })"
      >
        <span class="i-lucide-arrow-left size-4" />
        Back
      </button>
      <span class="text-n-weak">|</span>
      <div>
        <h1 class="text-lg font-semibold text-n-slate-12">Create Template</h1>
        <p class="text-xs text-n-slate-10">Submit a new WhatsApp message template for Meta review</p>
      </div>
    </div>

    <!-- Success banner -->
    <div
      v-if="submitSuccess"
      class="mx-6 mt-4 flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-400 text-sm"
    >
      <span class="i-lucide-check-circle-2 size-5 shrink-0" />
      Template submitted successfully! Redirecting...
    </div>

    <!-- Main split layout -->
    <div class="flex flex-1 w-full min-w-0 overflow-hidden">
      <!-- Form panel -->
      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-6 min-w-0">

        <!-- Section: Inbox & Meta -->
        <div class="bg-n-solid-1 border border-n-weak rounded-xl p-5 flex flex-col gap-4">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-settings-2 size-4 text-woot-500" />
            Template Settings
          </h2>

          <!-- Inbox -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">WhatsApp Inbox</label>
            <div class="relative flex items-center">
              <select
                id="builder-inbox"
                v-model="selectedInboxId"
                class="w-full text-sm border border-n-weak rounded-lg px-3 pr-9 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 [appearance:none] [-webkit-appearance:none] bg-none font-medium cursor-pointer"
              >
                <option
                  v-for="inbox in whatsappCloudInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <div class="absolute right-3 inset-y-0 flex items-center justify-center text-n-slate-10 pointer-events-none z-10">
                <span class="i-lucide-chevron-down size-4" />
              </div>
            </div>
          </div>

          <!-- Name -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">Template Name</label>
            <input
              id="template-name"
              v-model="templateName"
              type="text"
              placeholder="e.g. order_confirmation"
              class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
            <p class="text-xs text-n-slate-10">Lowercase letters, numbers and underscores only</p>
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">Category</label>
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="cat in CATEGORIES"
                :key="cat.value"
                :class="[
                  'flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all',
                  category === cat.value
                    ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                    : 'border-n-weak hover:border-n-slate-8 bg-n-background'
                ]"
                @click="category = cat.value"
              >
                <span :class="[cat.icon, 'size-5', category === cat.value ? 'text-woot-600' : 'text-n-slate-10']" />
                <span :class="['text-xs font-medium', category === cat.value ? 'text-woot-700 dark:text-woot-400' : 'text-n-slate-12']">
                  {{ cat.label }}
                </span>
                <span class="text-[10px] text-n-slate-10 leading-tight">{{ cat.desc }}</span>
              </button>
            </div>
          </div>

          <!-- Language -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-medium text-n-slate-11">Language</label>
            <div class="relative flex items-center">
              <select
                id="template-language"
                v-model="language"
                class="w-full text-sm border border-n-weak rounded-lg px-3 pr-9 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 [appearance:none] [-webkit-appearance:none] bg-none font-medium cursor-pointer"
              >
                <option
                  v-for="lang in LANGUAGES"
                  :key="lang.value"
                  :value="lang.value"
                >
                  {{ lang.label }}
                </option>
              </select>
              <div class="absolute right-3 inset-y-0 flex items-center justify-center text-n-slate-10 pointer-events-none z-10">
                <span class="i-lucide-chevron-down size-4" />
              </div>
            </div>
          </div>
        </div>

        <!-- Section: Header -->
        <div class="bg-n-solid-1 border border-n-weak rounded-xl p-5 flex flex-col gap-4">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-type size-4 text-woot-500" />
            Header
            <span class="ml-auto text-xs font-normal text-n-slate-10">Optional</span>
          </h2>

          <!-- Header type toggle -->
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="opt in ['NONE','TEXT','IMAGE','VIDEO','DOCUMENT']"
              :key="opt"
              :class="[
                'px-3 py-1 text-xs rounded-full border transition-all',
                headerType === opt
                  ? 'bg-woot-600 text-white border-woot-600'
                  : 'bg-n-background border-n-weak text-n-slate-11 hover:border-woot-400'
              ]"
              @click="headerType = opt"
            >
              {{ opt.charAt(0) + opt.slice(1).toLowerCase() }}
            </button>
          </div>

          <!-- Text header -->
          <div v-if="headerType === 'TEXT'">
            <input
              id="header-text"
              v-model="headerText"
              type="text"
              maxlength="60"
              placeholder="Header text (max 60 chars)"
              class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
            <p class="text-xs text-n-slate-10 mt-1 text-right">{{ headerText.length }}/60</p>
          </div>

          <!-- Media header -->
          <div
            v-if="['IMAGE','VIDEO','DOCUMENT'].includes(headerType)"
            class="flex items-center gap-3 p-3 bg-n-background rounded-lg border border-dashed border-n-slate-8 text-n-slate-10"
          >
            <span
              :class="headerType === 'IMAGE' ? 'i-lucide-image' : headerType === 'VIDEO' ? 'i-lucide-video' : 'i-lucide-file'"
              class="size-5"
            />
            <p class="text-xs">
              Meta will request a sample {{ headerType.toLowerCase() }} during approval.
              The actual media URL is provided when sending the template.
            </p>
          </div>
        </div>

        <!-- Section: Body -->
        <div class="bg-n-solid-1 border border-n-weak rounded-xl p-5 flex flex-col gap-4">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-align-left size-4 text-woot-500" />
            Body
            <span class="ml-auto text-xs font-normal text-red-500">Required</span>
          </h2>

          <div class="relative">
            <textarea
              id="body-text"
              v-model="bodyText"
              rows="5"
              maxlength="1024"
              placeholder="Write your message body here. Use {{1}}, {{2}}, ... for variables."
              class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 resize-none"
            />
            <div class="flex items-center justify-between mt-1">
              <button
                class="flex items-center gap-1 px-2.5 py-1 text-xs text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded-lg border border-woot-200 dark:border-woot-800 transition-colors"
                @click="insertVariable"
              >
                <span class="i-lucide-plus size-3" />
                Add variable
              </button>
              <span class="text-xs text-n-slate-10">{{ bodyText.length }}/1024</span>
            </div>
          </div>

          <!-- Variable examples -->
          <div
            v-if="bodyVariables.length > 0"
            class="flex flex-col gap-2"
          >
            <p class="text-xs font-medium text-n-slate-11">Variable Examples (required by Meta)</p>
            <div
              v-for="v in bodyVariables"
              :key="v.index"
              class="flex items-center gap-2"
            >
              <span class="text-xs font-mono bg-n-background border border-n-weak px-2 py-1 rounded text-n-slate-12 shrink-0">
                {{ varLabel(v.index) }}
              </span>
              <input
                :id="'var-example-' + v.index"
                v-model="v.example"
                type="text"
                :placeholder="varPlaceholder(v.index)"
                class="flex-1 text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
          </div>
        </div>

        <!-- Section: Footer -->
        <div class="bg-n-solid-1 border border-n-weak rounded-xl p-5 flex flex-col gap-4">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-minus size-4 text-woot-500" />
            Footer
            <span class="ml-auto text-xs font-normal text-n-slate-10">Optional</span>
          </h2>
          <input
            id="footer-text"
            v-model="footerText"
            type="text"
            maxlength="60"
            placeholder="e.g. Powered by Zentry"
            class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <!-- Section: Buttons -->
        <div class="bg-n-solid-1 border border-n-weak rounded-xl p-5 flex flex-col gap-4">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-square-mouse-pointer size-4 text-woot-500" />
            Buttons
            <span class="ml-auto text-xs font-normal text-n-slate-10">Optional · max 3</span>
          </h2>

          <!-- Existing buttons -->
          <div
            v-for="(btn, idx) in buttons"
            :key="idx"
            class="flex flex-col gap-2 p-3 bg-n-background rounded-lg border border-n-weak"
          >
            <div class="flex items-center justify-between">
              <span class="flex items-center gap-1.5 text-xs font-medium text-n-slate-12">
                <span
                  :class="BUTTON_TYPES.find(t => t.value === btn.type)?.icon"
                  class="size-3.5"
                />
                {{ BUTTON_TYPES.find(t => t.value === btn.type)?.label }}
              </span>
              <button
                class="text-red-400 hover:text-red-600 p-0.5"
                @click="removeButton(idx)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
            <input
              v-model="btn.text"
              type="text"
              maxlength="25"
              placeholder="Button label"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
            <input
              v-if="btn.type === 'URL'"
              v-model="btn.url"
              type="url"
              placeholder="https://example.com"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
            <input
              v-if="btn.type === 'PHONE_NUMBER'"
              v-model="btn.phone"
              type="tel"
              placeholder="+1234567890"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>

          <!-- Add button -->
          <div
            v-if="buttons.length < 3"
            class="flex flex-wrap gap-2"
          >
            <button
              v-for="btype in BUTTON_TYPES"
              :key="btype.value"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-dashed border-n-slate-8 rounded-lg hover:border-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 text-n-slate-11 hover:text-woot-600 transition-all"
              @click="addButton(btype.value)"
            >
              <span class="i-lucide-plus size-3" />
              <span :class="btype.icon" class="size-3.5" />
              {{ btype.label }}
            </button>
          </div>
        </div>

        <!-- Validation errors -->
        <div
          v-if="validationErrors.length > 0"
          class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
        >
          <p class="text-xs font-semibold text-red-600 dark:text-red-400 mb-2">Please fix the following:</p>
          <ul class="list-disc list-inside flex flex-col gap-1">
            <li
              v-for="e in validationErrors"
              :key="e"
              class="text-xs text-red-500"
            >
              {{ e }}
            </li>
          </ul>
        </div>

        <!-- Submit error -->
        <div
          v-if="submitError"
          class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400"
        >
          <strong>Meta API Error:</strong> {{ submitError }}
        </div>

        <!-- Submit button -->
        <div class="flex justify-end gap-3 pb-6">
          <button
            class="px-5 py-2.5 text-sm border border-n-weak rounded-lg hover:bg-n-solid-2 text-n-slate-11 transition-colors"
            @click="router.push({ name: 'whatsapp_templates_index' })"
          >
            Cancel
          </button>
          <button
            id="btn-submit-template"
            :disabled="submitting || validationErrors.length > 0 || submitSuccess"
            class="flex items-center gap-2 px-5 py-2.5 text-sm bg-woot-600 hover:bg-woot-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            @click="submit"
          >
            <span
              v-if="submitting"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span
              v-else
              class="i-lucide-send size-4"
            />
            Submit for Review
          </button>
        </div>
      </div>

      <!-- Live preview panel -->
      <div class="w-80 border-l border-n-weak bg-n-solid-1 flex flex-col shrink-0 overflow-y-auto">
        <div class="px-4 py-3 border-b border-n-weak">
          <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Live Preview</p>
          <p class="text-xs text-n-slate-10 mt-0.5">WhatsApp message preview</p>
        </div>

        <!-- Fake WA conversation -->
        <div class="flex-1 p-4 bg-[#e5ddd5] dark:bg-[#0d1117] relative overflow-y-auto">
          <!-- Background pattern via CSS class only -->
          <div class="absolute inset-0 opacity-5 bg-[length:40px_40px]" />

          <div class="relative flex flex-col items-end gap-1 mt-2">
            <!-- Bubble -->
            <div class="bg-white dark:bg-[#202c33] rounded-2xl rounded-tr-sm shadow-md max-w-[240px] overflow-hidden">
              <!-- Header -->
              <div v-if="previewHeader">
                <div
                  v-if="previewHeader.type === 'text'"
                  class="px-3 pt-3 pb-1 font-semibold text-gray-800 dark:text-gray-100 text-sm"
                >
                  {{ previewHeader.content }}
                </div>
                <div
                  v-else
                  class="flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-4 text-gray-400 dark:text-gray-500"
                >
                  <span
                    :class="previewHeader.format === 'IMAGE' ? 'i-lucide-image' : previewHeader.format === 'VIDEO' ? 'i-lucide-video' : 'i-lucide-file'"
                    class="size-6"
                  />
                  <span class="text-xs">{{ previewHeader.format }}</span>
                </div>
              </div>

              <!-- Body -->
              <div class="px-3 py-2.5">
                <p
                  v-if="previewBodyHtml"
                  class="text-sm text-gray-800 dark:text-gray-100 leading-snug"
                  v-html="previewBodyHtml"
                />
                <p
                  v-else
                  class="text-sm text-gray-300 dark:text-gray-600 italic"
                >
                  Body text will appear here...
                </p>

                <!-- Footer -->
                <p
                  v-if="footerText"
                  class="text-xs text-gray-400 mt-1.5"
                >
                  {{ footerText }}
                </p>

                <!-- Timestamp -->
                <div class="flex justify-end mt-1">
                  <span class="text-[10px] text-gray-400">4:30 PM ✓✓</span>
                </div>
              </div>

              <!-- Buttons -->
              <div
                v-if="buttons.length > 0"
                class="border-t border-gray-100 dark:border-gray-600"
              >
                <div
                  v-for="btn in buttons"
                  :key="btn.text"
                  class="flex items-center justify-center gap-1.5 py-2.5 border-t border-gray-100 dark:border-gray-700 first:border-t-0 text-blue-500 text-sm cursor-default"
                >
                  <span
                    :class="btn.type === 'URL' ? 'i-lucide-external-link' : btn.type === 'PHONE_NUMBER' ? 'i-lucide-phone' : 'i-lucide-message-square-reply'"
                    class="size-3.5"
                  />
                  {{ btn.text || 'Button text' }}
                </div>
              </div>
            </div>

            <!-- WA tail -->
            <div class="flex items-center gap-1.5 text-gray-400 text-[10px] mr-1">
              <span>{{ category }}</span>
              <span>·</span>
              <span>{{ language }}</span>
            </div>
          </div>
        </div>

        <!-- Template meta summary -->
        <div class="px-4 py-3 border-t border-n-weak flex flex-col gap-1.5">
          <div class="flex items-center justify-between text-xs">
            <span class="text-n-slate-10">Name</span>
            <span class="text-n-slate-12 font-mono truncate max-w-[160px]">
              {{ templateName || '—' }}
            </span>
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="text-n-slate-10">Components</span>
            <span class="text-n-slate-12">
              {{ buildComponents().map(c => c.type).join(', ') || '—' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
